import React from "react";
import { useSelector, useDispatch } from "react-redux";

import { showNotification } from "jazasoft";
import { required } from "@jazasoft/mui-table";

import useMutation from "../../../hooks/useMutation";
import useQuery from "../../../hooks/useQuery";
import { getDistinctValues, isEmpty, isTrue, listFromCsv } from "../../../utils/helpers";
import useDeepMemo from "../../../hooks/useDeepMemo";
import { useSetting } from "../../../hooks";
import SettingKeys from "../../../utils/SettingKeys";
import { Entity } from "../../../utils/types";
import useWarehouse from "../../../hooks/useWarehouse";
import { FabClassification, RollForm, UOM } from "../../../constants";

const joinChar = "#@#";
const colorMapper = e => ({ id: `${e.color}${!isEmpty(e.code) ? `${joinChar}${e.code}` : ""}`, name: e.effColor });

const uomWidthChoices = [
  { id: UOM.CENTIMETER.value, name: UOM.CENTIMETER.label },
  { id: UOM.INCH.value, name: UOM.INCH.label }
];

const orderingCSVChoices = [
  { id: "No", name: "No" },
  { id: "Yes", name: "Yes" },
  { id: "Grade 3", name: "Grade 3" },
  { id: "Grade 3-4", name: "Grade 3-4" },
  { id: "Grade 4", name: "Grade 4" }
];

const uomLengthChoices = [
  { id: UOM.KILOGRAM.value, name: UOM.KILOGRAM.label },
  { id: UOM.METER.value, name: UOM.METER.label },
  { id: UOM.YARD.value, name: UOM.YARD.label }
];

const rollFormChoices = [
  { id: RollForm.OPEN, name: RollForm.OPEN },
  { id: RollForm.TUBE, name: RollForm.TUBE }
];

const useCreateEdit = props => {
  const { id, i18nKey, history, page = "create" } = props;

  const [fabricMap, setFabricMap] = React.useState({});

  const [mutate, { loading: saving }] = useMutation();

  const dispatch = useDispatch();
  const formValues = useSelector(state => state?.form && state.form["record-form"]?.values);
  const { whChoices } = useWarehouse();

  const fabricItemFields = useSetting(SettingKeys.ENTITY_FIELDS, Entity.FABRIC_ITEMS);
  const allowedCsvValues = useSetting(SettingKeys.ALLOWED_CSV_VALUES) || "";
  const allowedCsvList = listFromCsv(allowedCsvValues) || [];

  //api call
  const { data: invoice = {} } = useQuery(id && { url: `invoices/${id}` });

  const fabricIds = invoice?.fabricItemList?.map(e => e.fabric?.id).filter(id => !isEmpty(id)) || [];

  const { data: fabricList = [] } = useQuery(
    !isEmpty(fabricIds) && { url: "fabrics", params: { search: `id=in=(${fabricIds?.join(",")})`, size: 2000 } }
  );

  const columns = useDeepMemo(() => {
    if (page === "edit" && isEmpty(invoice?.fabricItemList)) return [];

    const fetchFabricChoices = (searchText, rows) => {
      const ids = Array.isArray(rows) ? getDistinctValues(rows.map(row => row.fabricId).filter(id => !isEmpty(id))) : [];
      return new Promise(resolve => {
        const options = {
          url: "fabrics",
          method: "get",
          params: {
            search: isEmpty(searchText)
              ? isEmpty(ids)
                ? ""
                : `id=in=(${ids.join(",")})`
              : `(itemCode=='*${searchText}*',itemDesc=='*${searchText}*',articleName=='*${searchText}*')`
          }
        };
        mutate(options, {
          onSuccess: fabricList => {
            const choices = fabricList.map(({ id, itemCode }) => ({ id, name: itemCode }));
            setFabricMap(prev => ({
              ...prev,
              ...fabricList.reduce((acc, e) => ({ ...acc, [e.id]: e }), {})
            }));
            resolve(choices);
          }
        });
      });
    };

    const columnList = [
      {
        dataKey: "fabricId",
        title: "Item Code",
        inputType: "auto-complete-input",
        fetchChoices: fetchFabricChoices,
        options: { style: { width: 160 } },
        validate: [required()],
        disabled: row => isTrue(row?.qcStarted || row?.rollAllocated)
      },
      {
        dataKey: "color",
        title: "Fabric Color",
        inputType: "select-input",
        choices: ({ row }) => (!isEmpty(row) ? row?.colorList?.map(colorMapper) : []),
        render: (value, _, row) => row.effFabColor || row?.colorList?.map(colorMapper)?.find(c => c.id === value)?.name,
        validate: [required()],
        disabled: row => isTrue(row?.qcStarted || row?.rollAllocated)
      },
      { dataKey: "itemDesc", title: "Item Desc" },
      { dataKey: "articleName", title: "Article \nName" },
      { dataKey: "classification", title: "Fabric \nClassification" },
      { dataKey: "construction", title: "Count \nConstruction" },
      { dataKey: "content", title: "Content" },
      {
        dataKey: "rollForm",
        title: "Roll Form",
        inputType: "select-input",
        choices: ({ row }) =>
          row.classification === FabClassification.KNITTED ? rollFormChoices : rollFormChoices.filter(c => c.id === RollForm.OPEN),
        validate: [required()],
        disabled: row => row.classification !== FabClassification.KNITTED || isTrue(row?.qcStarted || row?.rollAllocated)
      },
      {
        dataKey: "uomLength",
        title: "UOM Length",
        inputType: "select-input",
        choices: ({ row }) =>
          row.classification === FabClassification.KNITTED ? uomLengthChoices : uomLengthChoices.filter(c => c.id !== UOM.KILOGRAM.value),
        validate: [required()],
        disabled: row => isTrue(row?.qcStarted || row?.rollAllocated)
      },
      {
        dataKey: "uomWidth",
        title: "UOM \nWidth",
        inputType: "select-input",
        choices: uomWidthChoices,
        validate: [required()],
        disabled: row => isTrue(row?.qcStarted || row?.rollAllocated)
      },
      {
        dataKey: "colorShade",
        title: "Color Shade",
        inputType: "text-input",
        disabled: row => isTrue(row?.qcStarted || row?.rollAllocated)
      },
      {
        dataKey: "orderingWidth",
        title: "Booking \nWidth",
        inputType: "text-input",
        options: { type: "number" },
        validate: [required()],
        disabled: row => isTrue(row?.qcStarted || row?.rollAllocated)
      },

      {
        dataKey: "orderingGsm",
        title: "Booking \nGSM",
        inputType: "text-input",
        options: { type: "number" },
        disabled: row => isTrue(row?.qcStarted || row?.rollAllocated)
      },
      {
        dataKey: "orderingWarpShrinkage",
        title: "Ordering Warp\nShrinkage",
        inputType: "text-input",
        options: { type: "number" },
        disabled: row => isTrue(row?.qcStarted || row?.rollAllocated)
      },
      {
        dataKey: "orderingWeftShrinkage",
        title: "Ordering Weft\nShrinkage",
        inputType: "text-input",
        options: { type: "number" },
        disabled: row => isTrue(row?.qcStarted || row?.rollAllocated)
      },
      {
        dataKey: "orderingCsv",
        title: "Ordering \nCSV",
        inputType: "select-input",
        choices: orderingCSVChoices.filter(e => allowedCsvList.includes(e.id)),
        disabled: row => isTrue(row?.qcStarted || row?.rollAllocated)
      },
      {
        dataKey: "fpo",
        title: "FPO No.",
        inputType: "text-input",
        disabled: row => isTrue(row?.qcStarted || row?.rollAllocated)
      },
      {
        dataKey: "orderQty",
        title: "Fabric\nOrder Qty",
        inputType: "text-input",
        options: { type: "number" },
        disabled: row => isTrue(row?.qcStarted || row?.rollAllocated)
      },
      {
        dataKey: "invoiceQty",
        title: "Invoice Qty",
        inputType: "text-input",
        options: { type: "number" },
        validate: [required()],
        disabled: row => isTrue(row?.qcStarted || row?.rollAllocated)
      },
      {
        dataKey: "nominatedSupplier",
        title: "Nominated Supplier",
        inputType: "boolean-input",
        render: value => (isTrue(value) ? "Yes" : "No")
      },
      {
        dataKey: "warehouse",
        title: "Warehouse",
        inputType: "select-input",
        choices: whChoices,
        validate: [required()],
        disabled: row => isTrue(row?.qcStarted || row?.rollAllocated)
      }
    ];
    let finalFields = fabricItemFields;
    if (whChoices?.length !== 1) {
      finalFields.push("warehouse");
    }
    finalFields = finalFields.filter(e => e !== "poRef");

    return columnList.filter(c => finalFields.includes(c.dataKey));
  }, [page, fabricItemFields, invoice, mutate, whChoices]);

  const onSubmit = (rows, _, onSubmitComplete) => {
    let invoice = { ...formValues };
    invoice = {
      ...invoice,
      fabricItemList: rows.map((row, idx) => ({
        ...row,
        serial: idx + 1,
        color: row.color?.split(joinChar)[0],
        colorCode: row.color?.split(joinChar)[1]
      }))
    };

    const options = {
      url: isEmpty(id) ? "invoices" : `invoices/${id}`,
      method: isEmpty(id) ? "post" : "put",
      data: invoice
    };

    mutate(options, {
      onSuccess: data => {
        dispatch(
          showNotification(id ? "js.notification.updated" : "js.notification.created", {
            resource: i18nKey,
            messageArgs: { smart_count: 1 }
          })
        );
        history.push(`/warehouse/invoices/${data?.id}/view`);
        onSubmitComplete && onSubmitComplete();
      },
      onFailure: () => onSubmitComplete && onSubmitComplete()
    });
  };

  const rows = useDeepMemo(() => {
    if (isEmpty(invoice?.fabricItemList)) {
      return [{}];
    }

    const rows = invoice?.fabricItemList.map(fabricItem => {
      const fabric = fabricList.find(f => fabricItem?.fabric?.id === f.id);
      return {
        ...fabric,
        ...fabricItem,
        fabricId: fabric?.id,
        color: fabric?.colorList?.map(colorMapper).find(c => fabricItem.effFabColor === c.name)?.id,
        colorList: fabric?.colorList
      };
    });
    return rows.filter(e => isEmpty(e.parentId));
  }, [invoice, fabricList]);

  const onChange = (name, value, row, form) => {
    const dataKey = name.split(".")[1];

    if (dataKey === "fabricId") {
      const fabric = fabricMap[value] || fabricList?.find(f => value === f.id);
      if (isEmpty(fabric)) {
        form.change(`${name.split(".")[0]}.fabricId`, null);
        form.change(`${name.split(".")[0]}.itemCode`, null);
        form.change(`${name.split(".")[0]}.itemDesc`, null);
        form.change(`${name.split(".")[0]}.articleName`, null);
        form.change(`${name.split(".")[0]}.classification`, null);
        form.change(`${name.split(".")[0]}.construction`, null);
        form.change(`${name.split(".")[0]}.content`, null);
        form.change(`${name.split(".")[0]}.orderingWidth`, null);
        form.change(`${name.split(".")[0]}.rollForm`, null);
        // form.change(`${name.split(".")[0]}.orderingWarpShrinkage`, null);
        // form.change(`${name.split(".")[0]}.orderingWeftShrinkage`, null);
        form.change(`${name.split(".")[0]}.colorList`, []);
        form.change(`${name.split(".")[0]}.color`, null);
      } else {
        form.change(`${name.split(".")[0]}.fabricId`, value);
        form.change(`${name.split(".")[0]}.itemCode`, fabric.itemCode);
        form.change(`${name.split(".")[0]}.itemDesc`, fabric.itemDesc);
        form.change(`${name.split(".")[0]}.articleName`, fabric.articleName);
        form.change(`${name.split(".")[0]}.classification`, fabric.classification);
        form.change(`${name.split(".")[0]}.construction`, fabric.construction);
        form.change(`${name.split(".")[0]}.content`, fabric.content);
        form.change(`${name.split(".")[0]}.orderingWidth`, fabric.width);
        if (fabric.classification !== FabClassification.KNITTED) {
          form.change(`${name.split(".")[0]}.rollForm`, RollForm.OPEN);
        }
        // form.change(`${name.split(".")[0]}.orderingWarpShrinkage`, fabric.warpShrinkage);
        // form.change(`${name.split(".")[0]}.orderingWeftShrinkage`, fabric.weftShrinkage);
        form.change(`${name.split(".")[0]}.colorList`, fabric.colorList);
        if (fabric?.colorList?.length === 1) {
          form.change(`${name.split(".")[0]}.color`, fabric.colorList.map(colorMapper)[0]?.id);
        } else {
          form.change(`${name.split(".")[0]}.color`, null);
        }
      }
    }
  };

  return {
    saving,
    invoice,
    rows,
    columns,
    onSubmit,
    onChange
  };
};

export default useCreateEdit;
